/**
 * Where to send someone after they sign in, when they asked to go somewhere.
 *
 * `?next=` is attacker-controlled: it arrives in a URL, and the URL can be
 * handed to the person by anyone. Whatever comes back from here reaches a
 * `navigate()`, an `<a href>`, and — on the Google path — the `returnTo` the
 * OAuth flow carries. So this is an authorisation-shaped decision about
 * navigation, and it gets one definition.
 *
 * ── Why one definition ──────────────────────────────────────────────────────
 * There were three, byte-identical, in SignIn.tsx, ForgotPassword.tsx and
 * ResetPassword.tsx, each with a comment explaining that it was copied rather
 * than imported because the other file belonged to somebody else that week.
 * All three had the same gap, and fixing one would have fixed one. A security
 * predicate is the last thing in a codebase that should be duplicated to avoid
 * a merge conflict.
 *
 * ── What is rejected, and why each one matters ──────────────────────────────
 * The rule is: a path on THIS origin, or nothing.
 *
 *   `https://evil.example`  absolute — obviously off-origin.
 *   `//evil.example`        protocol-relative. `new URL('//evil.example', origin)`
 *                           resolves to `https://evil.example`, and a browser
 *                           treats it the same way in an href.
 *   `/\evil.example`        the one the original guard missed. It starts with a
 *                           single `/`, so the `startsWith('//')` test passes it
 *                           — but the URL parser normalises `\` to `/`, so it
 *                           resolves to `https://evil.example` exactly like the
 *                           line above. Verified, not assumed.
 *   `/%2f evil`             percent-encoded separators, decoded late by
 *                           something downstream.
 *
 * Anything that is not a plain, single-slash-prefixed path falls back to the
 * root. Failing closed costs a redirect; failing open hands somebody a
 * Rundown-branded link that lands on a page we do not control, which is the
 * whole of a phishing setup.
 */

/** Matches a `next` we are willing to navigate to. */
function isSafe(raw: string): boolean {
  // Must be a path, and only one leading slash.
  if (!raw.startsWith('/')) return false;
  if (raw.startsWith('//')) return false;
  // A backslash anywhere in the authority position is normalised to `/` by the
  // URL parser, so `/\host` and `/\\host` both escape the origin.
  if (raw.startsWith('/\\')) return false;
  // Control characters are stripped by some parsers before resolution, which
  // can turn `/\tevil` into something that is not a path any more.
  // eslint-disable-next-line no-control-regex
  if (/[ -]/.test(raw)) return false;
  // An encoded separator immediately after the leading slash is the encoded
  // spelling of the two cases above.
  if (/^\/(%2f|%5c)/i.test(raw)) return false;
  return true;
}

export function safeNextPath(raw: string | null | undefined): string {
  if (raw === null || raw === undefined || raw === '') return '/';
  return isSafe(raw) ? raw : '/';
}
